import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { TALENT as DEFAULT_TALENT } from "@/data/talent";
import { Seo } from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COMING_SOON = ["Roster / 03", "Roster / 04"];

const figureFrame = "relative bg-[#f4f4f4] border border-[#e5e5e5] overflow-hidden";
const captionClass =
  "absolute bottom-4 left-4 text-[11px] font-mono uppercase tracking-[.12em] text-[#666666] bg-white/90 px-3 py-1.5 border border-[#e5e5e5]";

const OurPeoplePage = () => {
  const [talent, setTalent] = useState(DEFAULT_TALENT);

  useEffect(() => {
    axios.get(`${API}/talent`).then((res) => {
      if (res.data.length) setTalent(res.data);
    }).catch(() => {});
  }, []);

  return (
    <>
      <Seo
        title="Our People — #whatshesaidproject"
        description="A selective roster of speakers, artists, and storytellers — Shannon Ivey, D. Nichole Davis, and Jenn Wilenta — available for keynotes, workshops, and bookings."
        path="/our-people"
      />
      {/* HERO */}
      <section className="bg-white text-[#121212] pt-12 pb-24 md:py-28 border-b border-[#e5e5e5]">
        <div className="max-w-6xl mx-auto px-6">
          <span className="text-xs font-mono uppercase tracking-[.18em] text-[#e02020] block mb-3">
            Our People
          </span>
          <h1
            data-testid="our-people-headline"
            className="font-disp text-[clamp(36px,5vw,72px)] leading-[1.02] tracking-tight font-normal text-[#121212] text-balance max-w-4xl mb-6"
          >
            The roster.
          </h1>
          <p className="text-[17px] leading-[1.65] text-[#555555] max-w-2xl">
            A selective, intentionally curated roster of speakers, artists, and storytellers —
            voices driving crucial conversations across stages, institutions, and cultural forums.
          </p>
        </div>
      </section>

      {/* ROSTER */}
      <section className="bg-[#fafafa] text-[#121212] py-24 md:py-32 border-b border-[#e5e5e5]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {talent.map((t) => (
              <article
                key={t.slug}
                data-testid={`roster-card-${t.slug}`}
                className="border border-[#e5e5e5] p-8 flex flex-col justify-between bg-white hover:border-[#121212] transition-colors"
              >
                <div>
                  <figure className={`${figureFrame} aspect-[4/3] mb-6`}>
                    <img
                      src={t.img}
                      alt={t.caption}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <figcaption className={captionClass}>{t.caption}</figcaption>
                  </figure>
                  <div className="flex items-baseline justify-between border-b border-[#e5e5e5] pb-3 mb-4">
                    <h2 className="font-disp text-2xl text-[#121212]">{t.name}</h2>
                    <span className="text-xs font-mono uppercase tracking-[.14em] text-[#e02020]">
                      {t.role}
                    </span>
                  </div>
                  <p className="text-sm text-[#444444] leading-relaxed mb-6">{t.focus}</p>
                </div>
                <Link
                  to={`/our-people/${t.slug}`}
                  data-testid={`roster-view-${t.slug}`}
                  className="inline-flex items-center text-xs font-mono uppercase tracking-[.14em] text-[#121212] underline underline-offset-4 hover:text-[#e02020] transition-colors"
                >
                  View Profile →
                </Link>
              </article>
            ))}

            {COMING_SOON.map((label) => (
              <article
                key={label}
                data-testid={`roster-card-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="border border-dashed border-[#cccccc] p-8 flex flex-col justify-between bg-[#fafafa]"
              >
                <div>
                  <figure className="relative aspect-[4/3] mb-6 border border-[#e5e5e5] bg-[#f4f4f4] flex items-center justify-center">
                    <span className="text-xs font-mono uppercase tracking-[.14em] text-[#6b6b6b]">
                      Portrait forthcoming
                    </span>
                  </figure>
                  <div className="flex items-baseline justify-between border-b border-[#e5e5e5] pb-3 mb-4">
                    <h2 className="font-disp text-2xl text-[#121212]">Coming Soon</h2>
                    <span className="text-xs font-mono uppercase tracking-[.14em] text-[#6b6b6b]">
                      {label}
                    </span>
                  </div>
                  <p className="text-sm text-[#555555] leading-relaxed mb-6">
                    The roster grows deliberately. Additional voices will be announced here.
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#121212] text-white py-24 md:py-32">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-8">
              <span className="text-xs font-mono uppercase tracking-[.18em] text-[#e02020] block mb-3">
                Book a Voice
              </span>
              <h2 className="font-disp text-[clamp(28px,3.5vw,48px)] leading-[1.05] tracking-tight font-normal text-white mb-6">
                Bring the work to your stage.
              </h2>
              <p className="text-[17px] text-[#aaaaaa] leading-relaxed max-w-2xl">
                Keynotes, performances, workshops, and facilitated conversations — tell us about
                your audience and your moment.
              </p>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <Link
                to="/connect"
                data-testid="our-people-cta-connect"
                className="inline-flex items-center justify-center px-6 py-3.5 bg-[#e02020] text-white text-xs font-mono uppercase tracking-[.14em] hover:bg-white hover:text-[#121212] transition-colors rounded-none"
              >
                Start a Booking Inquiry →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default OurPeoplePage;
